import { AsyncPipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ReactiveFormsModule, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule } from '@angular/material/legacy-input';
import { TranslateModule } from '@ngx-translate/core';
import { CustomisableVariables } from '@shared/models/customisable-variables';
import { CustomisableVariablesService } from '@shared/services/customisable-variables.service';
import { ProductHomepageService } from '@shared/services/product-homepage.service';
import { SiteSettingsService } from '@shared/services/site-settings.service';
import { ReCaptchaComponent } from '@shared/shared/components/re-captcha/re-captcha.component';
import { HomePageComponentNameEnum } from '@shared/shared/enums/homepage-components-name.enum';
import { ISiteSettings } from '@shared/shared/interfaces/site-settings.interfaces';
import { LanguageService } from '@shared/shared/services/language-service';
import { RecaptchaService } from '@shared/shared/services/recaptcha.service';
import { Angulartics2Segment } from 'angulartics2';
import { Observable } from 'rxjs';
import { filter, take } from 'rxjs/operators';

export interface EligibilityCheckDialogData {
  title: string;
  eligibilityCheckTracking?: string;
  translations: string[];
}

@Component({
  selector: 'ten-eligibility-check-dialog',
  templateUrl: './eligibility-check-dialog.component.html',
  styleUrls: ['./eligibility-check-dialog.component.scss'],
  standalone: true,
  imports: [
    MatDialogModule,
    NgClass,
    AsyncPipe,
    MatIconModule,
    MatLegacyInputModule,
    NgFor,
    ReactiveFormsModule,
    MatLegacyFormFieldModule,
    TranslateModule,
    MatLegacyButtonModule,
    NgIf,
    ReCaptchaComponent,
  ],
})
export class EligibilityCheckDialogComponent implements OnInit, OnDestroy {
  @ViewChild(ReCaptchaComponent, { static: false })
  public reCaptchaComponent: ReCaptchaComponent;
  public captchaLanguage: string;

  public form: UntypedFormGroup;
  public rightToLeftSite: boolean;
  public eligibilityType: string;
  public acceptEligibilityBin: boolean;
  public enableRecaptcha: boolean;

  public siteSettings$: Observable<ISiteSettings> = this.siteSettingsService.fetchSiteSettings();
  public customVariables$: Observable<CustomisableVariables> = this.customVariableService.variables;

  constructor(
    private languageService: LanguageService,
    private siteSettingsService: SiteSettingsService,
    private customVariableService: CustomisableVariablesService,
    private reCaptchaService: RecaptchaService,
    private angulartics2Segment: Angulartics2Segment,
    public productHomepageService: ProductHomepageService,
    public dialogRef: MatDialogRef<EligibilityCheckDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: EligibilityCheckDialogData
  ) {
    this.rightToLeftSite = this.languageService.isRtlLanguage();

    this.siteSettings$
      .pipe(
        filter((siteSettings: ISiteSettings) => this.siteSettingsService.settingsRetrieved && !!siteSettings.id),
        take(1)
      )
      .subscribe((siteSettings: ISiteSettings) => {
        this.captchaLanguage = siteSettings.language;
        this.eligibilityType = siteSettings?.eligibilities_and_selfregs;
        this.acceptEligibilityBin = siteSettings?.switches?.accept_bin_for_eligibility;
        this.enableRecaptcha =
          siteSettings?.switches?.enable_recaptchas && siteSettings?.switches?.enable_eligibility_recaptcha;
        this.loadForm();
      });
  }

  public ngOnInit() {
    this.productHomepageService.eligibilityCheckValidationErrors = [];
    this.loadForm();
    this.reCaptchaService.setReCaptcha(true);
  }

  private loadForm() {
    this.form = new UntypedFormGroup({
      cardNumber: new UntypedFormControl('', [
        Validators.required,
        ProductHomepageService.Validators.validCard(this.acceptEligibilityBin),
      ]),
    });
  }

  public onCrossClick() {
    this.dialogRef.close();
  }

  /**
   * Dispatch the card number entered by the user to be used in the relavant API call.
   */
  public onEligibilityDetailsSubmitted(token: string) {
    if (this.data.eligibilityCheckTracking) {
      this.angulartics2Segment.eventTrack(this.data.eligibilityCheckTracking, {});
    }

    const payload = {
      identifier: 'card_number',
      value: this.form.get('cardNumber').value.replace(/\s/g, ''),
      captchaToken: token,
    };

    this.productHomepageService
      .checkEligibility(
        payload,
        this.eligibilityType,
        HomePageComponentNameEnum.ProductHomepageMastercardComponent,
        false
      )
      .subscribe(
        () => this.dialogRef.close('success'),
        () => this.dialogRef.close('invalid')
      );
  }

  public onSubmit() {
    this.reCaptchaComponent.execute();
  }

  public ngOnDestroy() {
    this.reCaptchaService.setReCaptcha(false);
  }
}
